import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import * as setSnackBarActions from '@store/snack-bar/snack-bar.actions';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { selectErrorModal } from '@store/error-modal/error-modal.selectors';
import { Subject } from 'rxjs';
import { ErrorModalService } from '@shared/components/error-modal/error-modal.service';
import { selectCommonOrderReference } from '@store/common-order/common-order.selectors';
import { AppState } from '@store/state';
import { ExtrasInfo } from '@services/extras/_interfaces_/extras-info.interface';
import { ExtrasOverviewService } from '@shared/services/extras/extras-overview.service';
import { FullScreenLoaderEnum } from '@enums/fullscreen-loader.enum';
import { ModalExtrasComponent } from '../extras-modal/extras-modal.component';
import { Router } from '@angular/router';
import { SessionStorageEnum } from '@enums/session-storage.enum';
import { StepperBarStep } from '@shared/services/extras/_interfaces_/extras-steps.interface';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';

@Component({
  selector: 'app-overview-page',
  templateUrl: './overview-page.component.html',
  styleUrls: ['./overview-page.component.scss'],
})
export class OverviewPageComponent implements OnInit {
  private unsubscribe$ = new Subject<void>();
  flightDetails: ExtrasInfo;
  extrasSteps: Array<StepperBarStep>;
  flightIdentifier: string;
  culture: string;
  dossierNumber: string;
  orderReference: string;

  constructor(
    private extrasOverviewService: ExtrasOverviewService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private modalService: BsModalService,
    private store: Store<AppState>,
    private router: Router,
    private errorModalService: ErrorModalService
  ) {}

  ngOnInit(): void {
    this.flightDetails = this.extrasOverviewService.getExtrasData();
    if (this.flightDetails) {
      this.flightIdentifier = this.flightDetails.flightIdentifier;
    }

    this.translate.onLangChange.subscribe(() => {
      this.updateExtrasSteps();
      this.culture = this.translate.currentLang;
    });
    this.updateExtrasSteps();
    this.getOrderReference();
  }

  getOrderReference(): void {
    this.store
      .select(selectCommonOrderReference)
      .pipe(take(1))
      .subscribe((orderReference) => (this.orderReference = orderReference || ''));
  }

  openFullscreenLoader(): void {
    this.store.dispatch(
      fullscreenLoaderActions.setVisibility({
        isVisible: true,
        loaderType: FullScreenLoaderEnum.MODAL,
        contentfulMessageKey: 'EXTRAS_LOADER.HEADER',
      })
    );
    this.extrasOverviewService
      .confirmExtras(this.flightIdentifier)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.store.dispatch(
            setSnackBarActions.setSnackBar({
              snackBarSuccess: true,
              showSnackBar: true,
              snackBarMessage: this.translate.instant('EXTRAS_LOADER.SUCCESS_GENERIC_EXTRAS'),
            })
          );
          this.handleRedirectToOrderDetails();
        },
        (error) => {
          this.store.dispatch(setSnackBarActions.setSnackBar({ snackBarSuccess: false, showSnackBar: true, snackBarMessage: '' }));
          this.handleRedirectToOrderDetails();
        }
      );
  }

  openModal(success: boolean): void {
    const bsModalRef: BsModalRef = this.modalService.show(ModalExtrasComponent, {
      initialState: {
        success: success,
        class: 'modal-lg',
      },
    });
    bsModalRef.content.setModalData(success);
  }

  updateExtrasSteps(): void {
    this.extrasSteps = [
      { stepName: this.translate.instant('STEPBAR_EXTRAS.STEP_1') },
      { stepName: this.translate.instant('STEPBAR_EXTRAS.STEP_2') },
    ];
    this.cdRef.detectChanges();
  }

  navigateToCreateExtrasPage(): void {
    const storedParams = sessionStorage.getItem(SessionStorageEnum.CreatePageRouteQueryParams);
    this.router.navigate([UrlPathnameEnum.ExtrasPageCreate], { queryParams: JSON.parse(storedParams) });
  }

  finalize(): void {
    this.store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: false }));
    this.router.navigateByUrl(`${UrlPathnameEnum.CommonOrder}/${this.orderReference}`);
  }

  handleRedirectToOrderDetails() {
    this.store
      .select(selectErrorModal)
      .pipe(take(1))
      .subscribe((errorModal) => {
        if (errorModal.isVisible) {
          this.errorModalService.handleCloseEmitter.pipe(take(1), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.finalize();
          });
        } else {
          this.finalize();
        }
      });
  }
}
